"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { AlertTriangle } from "lucide-react";
import { FOLLOWED_PLACES_LIMITS } from "@/lib/tiers";

interface AccountShape {
  tier: "free" | "pro" | "agency";
  cancelAtPeriodEnd: boolean;
  periodEnd: string | null;
  followedPlacesCount: number;
}

function formatPeriodEnd(iso: string): string {
  const d = new Date(iso);
  const days = Math.max(
    0,
    Math.round((d.getTime() - Date.now()) / (24 * 60 * 60 * 1000)),
  );
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `on ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

/**
 * Surfaces a warning while a Pro/Agency user is in the Stripe grace period
 * (cancel_at_period_end = true, subscription still active). If their follow
 * count is above the Free limit, spell out what gets removed on switchover
 * and offer Resume Pro. Silent otherwise, including when they're already on
 * Free or haven't cancelled.
 *
 * Mount once in the authenticated app shell — fetches /api/account itself,
 * so pages don't need to plumb anything through.
 */
export function SubscriptionDowngradeBanner() {
  const [account, setAccount] = useState<AccountShape | null>(null);

  useEffect(() => {
    let active = true;
    fetch("/api/account")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (active && d && typeof d.tier === "string") setAccount(d as AccountShape);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (!account) return null;
  if (!account.cancelAtPeriodEnd || !account.periodEnd) return null;

  const freeLimit = FOLLOWED_PLACES_LIMITS.free;
  const willLose = Math.max(0, account.followedPlacesCount - freeLimit);
  if (willLose === 0) return null;

  const when = formatPeriodEnd(account.periodEnd);

  return (
    <div className="mx-auto mb-[var(--space-4)] max-w-[var(--container-lg)] px-[var(--space-4)]">
      <div
        role="alert"
        className="flex flex-col gap-[var(--space-3)] rounded-[var(--radius-lg)] border border-[var(--color-warning)]/30 bg-[var(--color-warning-subtle)] px-[var(--space-4)] py-[var(--space-3)] sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-start gap-[var(--space-2)]">
          <AlertTriangle
            className="mt-[2px] size-4 shrink-0 text-[var(--color-warning)]"
            aria-hidden="true"
          />
          <p className="text-[length:var(--font-size-sm)] text-[var(--color-text-primary)]">
            Your Pro plan ends {when}. When it does, we&apos;ll keep your most
            recently followed place and remove{" "}
            <span className="font-[number:var(--font-weight-semibold)]">
              {willLose} other {willLose === 1 ? "place" : "places"}
            </span>
            {" "}along with tasks, notes, and insights for those.
          </p>
        </div>
        <Link
          href="/account"
          className="shrink-0 inline-flex items-center justify-center rounded-[var(--radius-full)] bg-[var(--color-warning)] px-[var(--space-4)] py-[var(--space-1-5)] text-[length:var(--font-size-xs)] font-[number:var(--font-weight-semibold)] text-white hover:opacity-90 transition-opacity"
        >
          Resume Pro
        </Link>
      </div>
    </div>
  );
}
